import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CategoryResponseDto, FamilyResponseDto, FamilyService } from 'src/app/backend';
import { SaveDonationDialogComponent } from 'src/app/shared/dialogs/save-donation-dialog/save-donation-dialog.component';
import { DonationService } from '../../backend/api/donation.service';
import { BasePagedComponent } from '../../core/components/base-paged/base-paged.component';
import { DialogService } from '../../core/dialog/dialog.service';
import { AuthService } from '../../core/services/auth.service';
import { ConfirmDialogComponent } from '../../shared/dialogs/confirm-dialog/confirm-dialog.component';
import { UntypedFormControl } from '@angular/forms';
import moment from 'moment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../../environments/environment';

declare var paypal: any;

interface DonationRow {
  id: number;
  name: string;
  note: string;
  date: any;
  family: FamilyResponseDto;
  category: CategoryResponseDto;
}

@Component({
  selector: 'app-donations',
  templateUrl: './donations.component.html',
  styleUrls: ['./donations.component.scss'],
})
export class DonationsComponent extends BasePagedComponent implements OnInit {
  data: DonationRow[] = [];
  searchQueryControl = new UntypedFormControl('');
  isLoading = false;
  donationForm: FormGroup;
  showPaypalButton = false;

  constructor(
    private dialogService: DialogService,
    private donationsService: DonationService,
    authService: AuthService,
    t: TranslateService,
    messageService: NzMessageService,
    private fb: FormBuilder
  ) {
    super(authService, t, messageService);
    this.donationForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      amount: ['', [Validators.required, Validators.min(1)]]
    });
  }

  ngOnInit() {
    this.loadData();
    this.loadPaypalScript();
  }

  loadData() {
    this.isLoading = true;

    this.donationsService
      .apiDonationGet({
        ...this.tableData,
        filter: JSON.stringify({
          search: this.searchQueryControl.value,
        }),
      })
      .subscribe(
        (res) => {
          this.pageLoaded(res);
          this.isLoading = false;
          this.data =
            res.results?.map((x) => ({
              id: x.id || 0,
              name: x.name || '',
              note: x.note || '',
              date: moment(x.donationDate).format('MM/DD/YYYY'),
              family: x.family || {},
              category: x.category || {}
            })) || [];
        },
        (err) => {
          this.isLoading = false;
        }
      );
  }

  searchDonations() {
    this.loadData();
  }

  new() {
    this.dialogService
      .open(SaveDonationDialogComponent, {
        size: 'md',
        closeOnClickOutside: false,
      })
      .afterClosed.subscribe((res) => {
        if (res?.saved) {
          this.loadData();
        }
      });
  }

  edit(id: number) {
    this.dialogService
      .open(SaveDonationDialogComponent, {
        size: 'md',
        closeOnClickOutside: false,
        data: {
          id,
        },
      })
      .afterClosed.subscribe((res) => {
        if (res?.saved) {
          this.loadData();
        }
      });
  }

  delete(id: number) {
    this.dialogService
      .open(ConfirmDialogComponent, {
        size: 'sm',
      })
      .afterClosed.subscribe((res) => {
        if (res?.confirmed) {
          this.donationsService.apiDonationIdDelete({ id }).subscribe((res) => {
            this.loadData();
          });
        }
      });
  }

  loadPaypalScript() {
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${environment.paypalClientId}&currency=USD`;
    script.async = true;
    script.onload = () => {
      this.initializePaypalButton();
    };
    document.body.appendChild(script);
  }

  initializePaypalButton() {
    paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'blue',
        shape: 'rect',
        label: 'donate'
      },
      createOrder: (data: any, actions: any) => {
        return actions.order.create({
          purchase_units: [{
            amount: {
              value: this.donationForm.get('amount')?.value
            },
            description: `Donation from ${this.donationForm.get('name')?.value}`
          }]
        });
      },
      onApprove: (data: any, actions: any) => {
        return actions.order.capture().then((details: any) => {
          alert('Thank you for your donation!');
          this.donationForm.reset();
        });
      }
    }).render('#paypal-button-container');
  }

  onSubmit() {
    if (this.donationForm.valid) {
      this.showPaypalButton = true;
    }
  }
}
